import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'

export interface PasskeyRegistrationResult {
  credentialId: string
  publicKey: ArrayBuffer
  prfKey: ArrayBuffer | null // PRF-derived encryption key (if available)
  hasPRF: boolean // Whether PRF was successfully used
}

export interface PasskeyAuthenticationResult {
  credentialId: string
  prfKey: ArrayBuffer | null // PRF-derived encryption key (if available)
  hasPRF: boolean // Whether PRF was successfully used
}

/**
 * Get the relying party ID based on the current environment
 */
function getRelyingPartyId(): string {
  const hostname = window.location.hostname
  // For localhost, use 'localhost'
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost'
  }
  // For production, use the actual domain
  return hostname
}

/**
 * Check if the browser supports WebAuthn PRF extension
 * Note: This is a best-effort check. We'll still try PRF during registration
 * and gracefully handle if it's not available.
 */
export async function isPRFSupported(): Promise<boolean> {
  if (!window.PublicKeyCredential) {
    return false
  }

  try {
    // Check if PRF extension is supported
    // Note: This API is not reliable across all browsers
    const extensions = await (PublicKeyCredential as any).getClientCapabilities?.()
    return extensions?.prf === true
  } catch {
    // PRF might still work even if this check fails
    return false
  }
}

/**
 * Generate a random challenge for WebAuthn (as base64url string)
 */
function generateChallenge(): string {
  const challenge = new Uint8Array(32)
  crypto.getRandomValues(challenge)
  // Convert to base64url format (what SimpleWebAuthn expects)
  return bufferToBase64URLString(challenge)
}

/**
 * Convert buffer to base64url string (RFC 4648)
 */
function bufferToBase64URLString(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64URLStringToBuffer(base64url: string): ArrayBuffer {
  // Add back padding and convert to standard base64
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const padLength = (4 - (base64.length % 4)) % 4
  const paddedBase64 = base64 + '='.repeat(padLength)

  const binary = atob(paddedBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Convert ArrayBuffer to base64 string (for backward compatibility)
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return bufferToBase64URLString(buffer)
}

/**
 * Convert base64 string to ArrayBuffer (for backward compatibility)
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  return base64URLStringToBuffer(base64)
}

/**
 * Register a new passkey with PRF extension for wallet encryption
 * Attempts to evaluate PRF during registration to avoid a second prompt
 */
export async function registerPasskey(
  userName: string,
  userId: string
): Promise<PasskeyRegistrationResult> {
  const rpId = getRelyingPartyId()

  // Generate challenge
  const challengeBuffer = new Uint8Array(32)
  crypto.getRandomValues(challengeBuffer)

  // Generate a deterministic PRF evaluation salt
  const prfSalt = new Uint8Array(32)
  prfSalt.fill(0) // Use zeros as a deterministic salt

  // Create registration options using native API
  // We use native API to support PRF evaluation during registration
  const publicKeyOptions: PublicKeyCredentialCreationOptions = {
    challenge: challengeBuffer,
    rp: {
      name: 'My Honcho Wallet',
      id: rpId,
    },
    user: {
      id: new TextEncoder().encode(userId),
      name: userName,
      displayName: userName,
    },
    pubKeyCredParams: [
      { type: 'public-key', alg: -7 }, // ES256
      { type: 'public-key', alg: -257 }, // RS256
    ],
    authenticatorSelection: {
      authenticatorAttachment: 'platform', // Prefer platform authenticators
      requireResidentKey: true,
      residentKey: 'required',
      userVerification: 'required',
    },
    timeout: 60000,
    attestation: 'none',
    // Request PRF extension WITH evaluation during registration
    // This attempts to get the PRF key in a single prompt
    extensions: {
      prf: {
        eval: {
          first: prfSalt, // Try to evaluate PRF during registration
        },
      },
    } as any,
  }

  try {
    // Use native WebAuthn API
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential | null

    if (!credential) {
      throw new Error('Failed to create passkey')
    }

    // Extract PRF results
    const clientExtResults = (credential as any).getClientExtensionResults()
    let prfKey: ArrayBuffer | null = null
    let hasPRF = false

    // Check if we got PRF output during registration (ideal case)
    if (clientExtResults?.prf?.results?.first) {
      prfKey = clientExtResults.prf.results.first as ArrayBuffer
      hasPRF = true
    } else if (clientExtResults?.prf?.enabled) {
      // PRF is supported but no eval result during registration
      // Will need to authenticate to get the key
      hasPRF = true
    }

    if (!hasPRF) {
      throw new Error(
        'WebAuthn PRF extension is not supported on this device. ' +
        'PRF is required for secure wallet encryption. ' +
        'Please use a device with PRF support (e.g., newer iOS/macOS with Touch ID/Face ID, Windows Hello, or compatible security keys).'
      )
    }

    return {
      credentialId: credential.id,
      publicKey: new ArrayBuffer(0), // Not needed for our use case
      prfKey, // May be null if browser doesn't support eval during registration
      hasPRF,
    }
  } catch (error) {
    console.error('Passkey registration error:', error)
    throw new Error(
      `Failed to register passkey: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

/**
 * Authenticate with an existing passkey to derive encryption key
 * Uses native WebAuthn API directly because PRF extension requires ArrayBuffer (not base64url)
 */
export async function authenticatePasskey(
  credentialId?: string
): Promise<PasskeyAuthenticationResult> {
  const rpId = getRelyingPartyId()

  // Generate challenge
  const challengeBuffer = new Uint8Array(32)
  crypto.getRandomValues(challengeBuffer)

  // Generate a deterministic PRF evaluation salt
  // Using a fixed salt ensures we get the same PRF output for the same credential
  const prfSalt = new Uint8Array(32)
  prfSalt.fill(0) // Use zeros as a deterministic salt

  // Create authentication options using native API format
  // Note: We must use native API because PRF extension requires ArrayBuffer
  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    challenge: challengeBuffer,
    rpId: rpId,
    timeout: 60000,
    userVerification: 'required',
    // PRF extension with ArrayBuffer salt (not base64url)
    extensions: {
      prf: {
        eval: {
          first: prfSalt, // Must be ArrayBuffer/Uint8Array, not string
        },
      },
    } as any,
  }

  // If credentialId is provided, only allow that specific credential
  if (credentialId) {
    publicKeyOptions.allowCredentials = [
      {
        id: base64URLStringToBuffer(credentialId),
        type: 'public-key',
        transports: ['internal', 'hybrid'],
      },
    ]
  }

  try {
    // Use native WebAuthn API directly
    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential | null

    if (!credential) {
      throw new Error('Failed to authenticate with passkey')
    }

    // Extract PRF result - this is the actual encryption key material
    const clientExtResults = (credential as any).getClientExtensionResults()
    let prfKey: ArrayBuffer | null = null
    let hasPRF = false

    if (clientExtResults?.prf?.results?.first) {
      // Successfully got PRF output - this is our secure encryption key
      prfKey = clientExtResults.prf.results.first as ArrayBuffer
      hasPRF = true
    }

    if (!hasPRF || !prfKey) {
      throw new Error(
        'Failed to derive encryption key from passkey. ' +
        'PRF extension is required but not available. ' +
        'Please ensure your device supports WebAuthn PRF.'
      )
    }

    return {
      credentialId: credential.id,
      prfKey,
      hasPRF,
    }
  } catch (error) {
    console.error('Passkey authentication error:', error)
    throw new Error(
      `Failed to authenticate with passkey: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}
